import React, { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import RedeemIcon from '@mui/icons-material/Redeem';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalPoshaka } from '../../contexts/PortalPoshakaContext';
import { CloseX, ignoreBackdropEscapeClose } from './portalUi';
import { poshakaBenefitImageUrls } from '../../utils/poshakaBenefitImages';

/**
 * Poshaka ribbon + benefits button — rendered beside the portal page title (see PortalPageHeader), right-aligned.
 */
export default function PortalPoshakaHeaderCluster() {
  const { staff } = useAuth();
  const { poshaka } = usePortalPoshaka();
  const [benefitsOpen, setBenefitsOpen] = useState(false);
  const { img1: IMG1, img2: IMG2 } = poshakaBenefitImageUrls();

  if (!poshaka?.isSubscriber) {
    return null;
  }

  const refLabel = staff?.subject
    ? `Ref: ${String(staff.subject).slice(0, 8)}…`
    : '—';

  const chipLabel = poshaka?.ribbon ? poshaka.ribbon : refLabel;

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.875}
        flexWrap="wrap"
        useFlexGap
        sx={{ justifyContent: { xs: 'flex-end', sm: 'flex-end' }, flexShrink: 0 }}
      >
        <Chip
          icon={
            poshaka?.isSubscriber ? (
              <MilitaryTechIcon sx={{ fontSize: '1.05rem !important' }} aria-hidden />
            ) : undefined
          }
          label={chipLabel}
          size="small"
          variant={poshaka?.isSubscriber ? 'filled' : 'outlined'}
          sx={
            poshaka?.isSubscriber
              ? {
                  maxWidth: { xs: 'min(100%, 320px)', sm: 364 },
                  height: 'auto',
                  py: 0.875,
                  pl: 0.5,
                  pr: 0.7,
                  borderRadius: 999,
                  border: '2px solid',
                  borderColor: '#7a5c10',
                  background: 'linear-gradient(165deg, #f5e6a8 0%, #e8c547 35%, #c9a227 70%, #a67c00 100%)',
                  color: '#1f1406',
                  boxShadow: '0 1px 6px rgba(166, 124, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.45)',
                  '& .MuiChip-icon': {
                    color: '#5c4018',
                    marginLeft: '7px',
                  },
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    display: 'block',
                    lineHeight: 1.4,
                    fontSize: '0.744rem',
                    fontWeight: 700,
                    paddingLeft: '7px',
                    paddingRight: '8px',
                  },
                }
              : {
                  maxWidth: { xs: 'min(100%, 280px)', sm: 294 },
                  height: 'auto',
                  py: 0.35,
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    display: 'block',
                    lineHeight: 1.35,
                    fontSize: '0.8125rem',
                  },
                }
          }
        />
        <Tooltip title="Poshaka benefits">
          <IconButton
            onClick={() => setBenefitsOpen(true)}
            aria-label="Open Poshaka benefits"
            size="small"
            sx={{
              width: 34,
              height: 34,
              padding: 0.5,
              border: '1.5px solid',
              borderColor: poshaka?.isSubscriber ? 'rgba(181, 134, 16, 0.85)' : 'primary.main',
              color: poshaka?.isSubscriber ? '#7a5c10' : 'primary.main',
              bgcolor: poshaka?.isSubscriber ? 'rgba(232, 197, 71, 0.12)' : 'transparent',
              '&:hover': {
                borderColor: poshaka?.isSubscriber ? '#a67c00' : 'primary.dark',
                bgcolor: poshaka?.isSubscriber ? 'rgba(232, 197, 71, 0.22)' : 'action.hover',
              },
            }}
          >
            <RedeemIcon sx={{ fontSize: '1.05rem' }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Dialog
        open={benefitsOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          setBenefitsOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span">Poshaka benefits</Typography>
          <CloseX onClick={() => setBenefitsOpen(false)} />
        </DialogTitle>
        <DialogContent dividers>
          {IMG1 ? (
            <Box
              component="img"
              src={IMG1}
              alt="Poshaka benefits"
              sx={{ width: '100%', height: 'auto', display: 'block', mb: IMG2 ? 2 : 0, borderRadius: 1 }}
            />
          ) : null}
          {IMG2 ? (
            <Box
              component="img"
              src={IMG2}
              alt="Poshaka benefits"
              sx={{ width: '100%', height: 'auto', display: 'block', borderRadius: 1 }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
